/**
 * The remember control — where f8's memory of one person is turned on and off
 * (PPL-350, contact detail).
 *
 * Four states, one component:
 *
 *   `never`   almost every synced contact. f8 knows what the address book knows
 *             and nothing more. This is the primary action on the page, and the
 *             line under it is where ppl learn what remembering actually is.
 *   `active`  the working state — settled, not celebratory.
 *   `paused`  dormant, not damaged. f8 still has everything it learned; it just
 *             stopped adding to it. Resuming is one click when there is a slot.
 *   premium   the same control with the counter absent entirely.
 *
 * ## Turning it off PAUSES
 *
 * It never deletes. The timeline, extractions and links all survive, so re-adding
 * costs nothing. No copy here may imply loss, and there is deliberately no "are
 * you sure you want to lose…" confirm — there is no loss to confirm. That is the
 * one place this differs from the timeline row beside it, which IS destructive
 * and therefore confirms first.
 *
 * ## Pronouns
 *
 * they/them throughout. The design canvas wrote "her" for every contact except a
 * hard-coded "dad", which would misgender most of an address book on the one
 * surface whose whole job is to feel like it knows the person.
 *
 * ## The window clause disappears when there is no window
 *
 * `memoryWindowDays` is null for premium AND for everyone while the fence is
 * still switched off server-side. In that state f8 genuinely does remember
 * everything, so "the last 30 days" would be false — the clause drops rather than
 * falling back to a number.
 */

import React from 'react';
import { Loader } from 'react-feather';
import { Moon, Sparkle } from './glyphs';
import { SlotPill } from './pills';
import { RT } from './tokens';
import { windowPhrase, type RememberQuota, type RememberState } from '../../services/rememberService';

interface Props {
  /** PERSONAL register — the name the app shows for this person. */
  firstName: string;
  state: RememberState;
  quota: RememberQuota | null;
  busy?: boolean;
  onRemember: () => void;
  onPause: () => void;
}

/* Shared across the premium and no-window footers so a copy pass can't edit
   one and miss the other; states pre-click what pausing actually stops. */
const PAUSE_TAIL = 'pausing stops new briefs — nothing is deleted.';

const primaryBtn: React.CSSProperties = {
  appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
  padding: '9px 15px', borderRadius: 999, border: '1px solid transparent',
  background: 'linear-gradient(135deg, #d946ef, #7c3aed)', color: '#fff',
  fontSize: 12.5, fontWeight: 600, boxShadow: '0 6px 18px rgba(217,70,239,0.28)',
};

const orbStyle = (bg: string, border: string): React.CSSProperties => ({
  width: 34, height: 34, borderRadius: 12, background: bg, border: `1px solid ${border}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});

export default function RememberControl({
  firstName, state, quota, busy = false, onRemember, onPause,
}: Props) {
  const who = firstName || 'them';
  const premium = quota?.isPremium === true;
  // Used ONLY to suppress the "one tap and they're back" reassurance when there
  // is no slot — never to skip the write. The server is what refuses, and it is
  // what emits `gate_hit`: short-circuiting here meant the callable was never
  // called on the commonest path, so the number the tier decision rests on only
  // ever counted the race case (PPL-351).
  const atLimit = !premium && quota?.remaining !== null && (quota?.remaining ?? 1) <= 0;
  const windowText = windowPhrase(quota?.memoryWindowDays ?? null);

  const panel = (bg: string, border: string): React.CSSProperties => ({
    borderRadius: 18, border: `1px solid ${border}`, background: bg, padding: 14, marginBottom: 16,
  });

  /* ── never remembered — the default, and the teaching moment ─────────── */
  if (state === 'never') {
    return (
      <div style={panel(RT.surfaceSoft, RT.border)}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <span style={orbStyle(RT.surface, RT.border)}><Sparkle size={15} color={RT.dimSolid} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
              f8 doesn&apos;t remember {who} yet
            </div>
            <div style={{ fontSize: 11.5, color: RT.muted, marginTop: 3, lineHeight: 1.5 }}>
              turn it on and f8 remembers their story — a brief before meetups on
              your calendar{windowText ? `, with ${windowText} in mind` : ''}.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button
            type="button"
            disabled={busy}
            onClick={onRemember}
            style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? <Loader size={12} className="spin" /> : <Sparkle size={12} color="#fff" />}
            remember {who}
          </button>
          <SlotPill small quota={quota} />
        </div>
      </div>
    );
  }

  /* ── paused — dormant, not damaged ───────────────────────────────────── */
  if (state === 'paused') {
    return (
      <div style={panel(RT.goldTint, RT.goldTintBorder)}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <span style={orbStyle('rgba(251,191,36,0.10)', RT.goldTintBorder)}>
            <Moon size={15} color={RT.gold} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>remembering is paused</div>
            <div style={{ fontSize: 11.5, color: RT.muted, marginTop: 3, lineHeight: 1.5 }}>
              f8 still has everything it learned about {who} — paused, not lost.
              no new briefs; their story is archived, never deleted.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button
            type="button"
            disabled={busy}
            onClick={onRemember}
            style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? <Loader size={12} className="spin" /> : null}
            resume remembering
          </button>
          <SlotPill small quota={quota} />
        </div>
        {!atLimit ? (
          <div style={{ fontSize: 10.5, color: RT.dim, marginTop: 9 }}>
            one click — their story picks up where it left off.
          </div>
        ) : null}
      </div>
    );
  }

  /* ── active, free and premium — same control, counter absent on premium ─ */
  return (
    <div style={panel(RT.magentaTint, RT.magentaTintBorder)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={orbStyle('rgba(217,70,239,0.16)', RT.magentaTintBorder)}>
          <Sparkle size={15} color={RT.magenta} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>f8 remembers {who}</div>
          <div style={{ fontSize: 11.5, color: RT.muted, marginTop: 2, lineHeight: 1.5 }}>
            remembering their story — a brief before meetups on your calendar.
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={onPause}
          aria-label={`pause remembering ${who}`}
          style={{
            appearance: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit',
            padding: '6px 12px', borderRadius: 999,
            background: RT.surface, border: `1px solid ${RT.border}`,
            color: RT.muted, fontSize: 11.5, fontWeight: 600, opacity: busy ? 0.6 : 1,
          }}
        >
          pause
        </button>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginTop: 11, paddingTop: 11,
        borderTop: `1px solid ${premium ? RT.magentaTintBorder : RT.border}`,
      }}>
        <SlotPill small quota={quota} />
        <span style={{ flex: 1, fontSize: 10.5, color: RT.dim, lineHeight: 1.45 }}>
          {premium
            ? `everyone, forever. ${PAUSE_TAIL}`
            : windowText
              ? `remembers ${windowText} — older memories are archived, never deleted.`
              : PAUSE_TAIL}
        </span>
      </div>
    </div>
  );
}
